import csv from 'csvtojson';
import {
	GhostPracticeCode,
	GhostPracticeCodeTypes,
} from '@models/ghost-practice';

// ===== Ghost Practice =====

export interface ParseGhostPracticeCodesOptions {
	csvContent: string;
	existingCodes?: GhostPracticeCode[];
}

export interface ParseGhostPracticeCodesResult {
	codes: GhostPracticeCode[];
}

/**
 * Parses Ghost Practice accounting codes from CSV content
 * @param options - CSV content and optional existing codes to merge with
 * @returns Parsed Ghost Practice codes
 * @throws Error if CSV format is invalid
 */
export async function parseGhostPracticeCodes(
	options: ParseGhostPracticeCodesOptions,
): Promise<ParseGhostPracticeCodesResult> {
	const { csvContent, existingCodes = [] } = options;

	const result = await csv({
		noheader: false,
		checkColumn: true,
		headers: [
			'type',
			'co',
			'br',
			'id',
			'descr',
			'glacct',
			'tax1',
			'tax2',
			'tax1rate',
			'tax2rate',
			'unitcharge',
			'fixcharge',
			'taxincl',
			'groupunder',
			'groupit',
			'taskcode',
			'tasklaw',
			'ratepct',
			'num1',
			'num2',
			'log1',
			'char1',
			'descrmemo',
			'billable',
		],
	}).fromString(csvContent);

	const codes = result.map(
		(row: {
			id: string;
			descr: string;
			glacct: string;
			tax1: string;
			type: string;
		}) => {
			const existingCode = existingCodes.find(
				(existing) => existing.id === row.id,
			);

			// Default type based on CSV type column: D = disbursement, F = fee
			let defaultType = GhostPracticeCodeTypes.FEE;
			if (row.type === 'D') {
				defaultType = GhostPracticeCodeTypes.DISBURSEMENT;
			}

			return {
				id: row.id,
				description: row.descr,
				glAccount: row.glacct,
				taxApplies: row.tax1 === 'Y',
				type: existingCode?.type ?? defaultType,
			};
		},
	);

	return { codes };
}
